import React, { useMemo } from "react";

import { projectTitleWithCode } from "../../utils/projectViews";

const SLICE_COLORS = [
  "#006a4e",
  "#00553f",
  "#b45309",
  "#7c3aed",
  "#0ea5e9",
  "#6366f1",
  "#dc2626",
  "#1565a8",
  "#ca8a04",
];

/** Minimum visual share (%) so tiny holdings remain visible and colored on the pie. */
const MIN_DISPLAY_PCT = 1.25;

function displayPercentages(slices) {
  if (!slices.length) return [];
  const needsBoost = slices.filter((s) => s.pct > 0 && s.pct < MIN_DISPLAY_PCT);
  if (!needsBoost.length) {
    return slices.map((s) => ({ ...s, displayPct: s.pct }));
  }
  const boostTotal = needsBoost.reduce((sum, s) => sum + (MIN_DISPLAY_PCT - s.pct), 0);
  const large = slices.filter((s) => s.pct >= MIN_DISPLAY_PCT);
  const largeTotal = large.reduce((sum, s) => sum + s.pct, 0);
  return slices.map((s) => {
    if (s.pct <= 0) return { ...s, displayPct: 0 };
    if (s.pct < MIN_DISPLAY_PCT) return { ...s, displayPct: MIN_DISPLAY_PCT };
    if (!largeTotal) return { ...s, displayPct: s.pct };
    return { ...s, displayPct: Math.max(0, s.pct - (s.pct / largeTotal) * boostTotal) };
  });
}

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function describeArc(cx, cy, r, startAngle, endAngle) {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArc = endAngle - startAngle <= 180 ? 0 : 1;
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y} Z`;
}

export function usePortfolioPieSlices(holdings, valueForHolding) {
  const slices = useMemo(() => {
    const rows = (holdings || [])
      .map((h, index) => {
        const value = Number(valueForHolding(h)) || 0;
        const label = projectTitleWithCode(h.project) || h.project?.name || "Project";
        return { id: h.id, label, value, color: SLICE_COLORS[index % SLICE_COLORS.length] };
      })
      .filter((row) => row.value > 0);
    const total = rows.reduce((sum, row) => sum + row.value, 0);
    return rows.map((row) => ({
      ...row,
      pct: total > 0 ? (row.value / total) * 100 : 0,
    }));
  }, [holdings, valueForHolding]);

  const paths = useMemo(() => {
    const withDisplay = displayPercentages(slices);
    if (!withDisplay.length) return [];
    const cx = 100;
    const cy = 100;
    const r = 88;
    let angle = 0;
    return withDisplay.map((slice) => {
      const sweep = (slice.displayPct / 100) * 360;
      const start = angle;
      const end = angle + sweep;
      angle = end;
      if (withDisplay.length === 1) {
        return {
          ...slice,
          d: `M ${cx} ${cy} m -${r}, 0 a ${r},${r} 0 1,0 ${r * 2},0 a ${r},${r} 0 1,0 -${r * 2},0`,
        };
      }
      return { ...slice, d: describeArc(cx, cy, r, start, end) };
    });
  }, [slices]);

  return { slices, paths };
}

export function PortfolioPieChartSvg({ paths, className = "" }) {
  if (!paths?.length) return null;
  return (
    <svg className={`investor-portfolio-pie__svg${className ? ` ${className}` : ""}`} viewBox="0 0 200 200" aria-hidden>
      {paths.map((slice) => (
        <path key={slice.id} d={slice.d} fill={slice.color} stroke="none" />
      ))}
    </svg>
  );
}

export function PortfolioPieChartLegend({ slices, formatMoney, className = "" }) {
  if (!slices?.length) return null;
  return (
    <ul className={`investor-portfolio-pie__legend${className ? ` ${className}` : ""}`}>
      {slices.map((slice) => (
        <li key={slice.id} className="investor-portfolio-pie__legend-item">
          <span className="investor-portfolio-pie__swatch" style={{ background: slice.color }} aria-hidden />
          <span className="investor-portfolio-pie__legend-label">{slice.label}</span>
          <span className="investor-portfolio-pie__legend-pct">{slice.pct.toFixed(1)}%</span>
          <span className="investor-portfolio-pie__legend-value muted">{formatMoney(slice.value)}</span>
        </li>
      ))}
    </ul>
  );
}

export default function PortfolioPieChart({
  holdings,
  valueForHolding,
  formatMoney,
  layout = "stacked",
}) {
  const { slices, paths } = usePortfolioPieSlices(holdings, valueForHolding);

  if (!slices.length) {
    return <p className="muted investor-portfolio-pie__empty">No holding value to chart yet.</p>;
  }

  const pieClass =
    layout === "chart-only"
      ? "investor-portfolio-pie investor-portfolio-pie--chart-only"
      : layout === "legend-only"
        ? "investor-portfolio-pie investor-portfolio-pie--legend-only"
        : "investor-portfolio-pie";

  if (layout === "legend-only") {
    return (
      <div className={pieClass} role="group" aria-label="Portfolio allocation breakdown">
        <PortfolioPieChartLegend slices={slices} formatMoney={formatMoney} />
      </div>
    );
  }

  return (
    <div className={pieClass} role="img" aria-label="Portfolio allocation by estimated value">
      <PortfolioPieChartSvg paths={paths} />
      {layout === "stacked" ? <PortfolioPieChartLegend slices={slices} formatMoney={formatMoney} /> : null}
    </div>
  );
}
